import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy, 
  getDocs,
  getDoc,
  Timestamp,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { Customer, CreateCustomerData, CustomerWithFieldValues } from '../../types/customer';
import { customerHistoryService } from './customerHistoryService';
import { generateCustomerCode } from '../../utils/formatters';

const COLLECTION = 'customers';

export const customerService = {
  async checkCustomerCodeExists(customerCode: string, excludeId?: string): Promise<boolean> {
    const q = query(
      collection(db, COLLECTION),
      where('customerCode', '==', customerCode)
    );
    const snapshot = await getDocs(q);
    
    // Ha van excludeId, akkor azt kizárjuk az ellenőrzésből (update esetén)
    if (excludeId) {
      return snapshot.docs.some(doc => doc.id !== excludeId);
    }
    
    return !snapshot.empty;
  },

  async create(data: CreateCustomerData, userId: string) {
    // Generáljuk a customerCode-ot, ha nincs megadva
    let customerCode = data.customerCode;
    if (!customerCode) {
      // Lekérjük az összes ügyfelet a kód generálásához
      const allCustomers = await this.getAll();
      customerCode = generateCustomerCode(allCustomers);
      console.log('Generated new customer code:', customerCode);
    } else {
      // Ha manuálisan megadták a kódot, ellenőrizzük az egyediséget
      const exists = await this.checkCustomerCodeExists(customerCode);
      if (exists) {
        throw new Error(`Az ügyfélazonosító (${customerCode}) már használatban van. Kérjük, válasszon másik kódot.`);
      }
    }

    const customerData = {
      ...data,
      customerCode,
      status: 'active' as const,
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: userId
    };

    const docRef = await addDoc(collection(db, COLLECTION), customerData);
    const createdId = docRef.id;
    
    // Record history entry for customer creation
    await customerHistoryService.recordChange(
      createdId,
      null,
      { ...customerData, id: createdId } as Partial<CustomerWithFieldValues>,
      userId,
      'create'
    );
    
    return createdId;
  },

  async update(id: string, data: Partial<Customer>, userId?: string) {
    const docRef = doc(db, COLLECTION, id);
    
    // Get current data to track changes
    const currentDocSnapshot = await getDoc(docRef);
    if (!currentDocSnapshot.exists()) {
      throw new Error(`Customer with ID ${id} not found`);
    }
    
    const currentData = { 
      id,
      ...currentDocSnapshot.data()
    } as CustomerWithFieldValues;
    
    // Ha módosítják a customerCode-ot, ellenőrizzük az egyediséget
    if (data.customerCode && data.customerCode !== currentData.customerCode) {
      const exists = await this.checkCustomerCodeExists(data.customerCode, id);
      if (exists) {
        throw new Error(`Az ügyfélazonosító (${data.customerCode}) már használatban van. Kérjük, válasszon másik kódot.`);
      }
    }
    
    const updateData = {
      ...data,
      ...(data.status && { isActive: data.status === 'active' }),
      organizations: data.organizations || [],
      updatedAt: serverTimestamp()
    };
    
    // Update the customer record
    await updateDoc(docRef, updateData);
    
    // Record history entry for update if we have a userId
    if (userId) {
      await customerHistoryService.recordChange(
        id,
        currentData,
        { ...currentData, ...updateData } as Partial<CustomerWithFieldValues>,
        userId,
        'update'
      );
    }
  },

  async delete(id: string, userId?: string): Promise<void> {
    const docRef = doc(db, COLLECTION, id);
    
    // Get current data to track deletion
    let currentData = null;
    if (userId) {
      const currentDocSnapshot = await getDoc(docRef);
      if (currentDocSnapshot.exists()) {
        currentData = { 
          id,
          ...currentDocSnapshot.data()
        } as CustomerWithFieldValues;
      }
    }
    
    // Delete the customer
    await deleteDoc(docRef);
    
    // Record history entry for deletion if we have a userId and data
    if (userId && currentData) {
      await customerHistoryService.recordChange(
        id,
        currentData,
        null,
        userId,
        'delete'
      );
    }
  },

  async getAll(): Promise<Customer[]> {
    const q = query(
      collection(db, COLLECTION),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      updatedAt: doc.data().updatedAt.toDate()
    })) as Customer[];
  },

  async getById(id: string): Promise<Customer | null> {
    const docRef = doc(db, COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return {
      id: docSnap.id,
      ...docSnap.data(),
      createdAt: docSnap.data().createdAt.toDate(),
      updatedAt: docSnap.data().updatedAt.toDate()
    } as Customer;
  },

  async getByCreator(userId: string): Promise<Customer[]> {
    const q = query(
      collection(db, COLLECTION),
      where('createdBy', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      updatedAt: doc.data().updatedAt.toDate()
    })) as Customer[];
  }
}; 